// Sign in. Live: the firm website's email and password (one shared Supabase login, ADR-0022), or a one-time email link; the firm
// adds people first (no self sign-up). Demo: pick a synthetic role.

export const RESET_PASSWORD_URL = "https://www.your-virtualcpa.com/forgot-password";
import { useState } from "react";
import { Button, Field, inputCls } from "../components";
import type { DataSource } from "../data/source";
import { titleCase } from "../lib/format";

export function SignInPage({ ds, onSignedIn }: { ds: DataSource; onSignedIn: () => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [useLink, setUseLink] = useState(!ds.signInWithPassword);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const people = ds.demoIdentities?.() ?? [];
  return (
    <main className="flex min-h-screen items-center justify-center bg-sunken p-4">
      <div className="w-full max-w-sm rounded-lg border border-line bg-raised p-6 shadow-sm">
        <div className="mb-4 flex items-center gap-2">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded bg-brand text-xs font-bold text-brand-ink" aria-hidden="true">YVC</span>
          <div><h1 className="text-lg font-semibold leading-tight">Sign in</h1><p className="text-xs text-muted">Your Virtual CPA — audit workspace and client portal</p></div>
        </div>
        {ds.mode === "demo" ? (
          <div className="space-y-3">
            <p className="text-sm text-muted">Demo data from a synthetic engagement. Choose who you are:</p>
            <ul className="space-y-2">
              {(people.length ? people : (["partner", "manager", "staff", "client"] as const).map((r) => ({ role: r, personName: titleCase(r), userId: r, firmId: "" }))).map((p) => (
                <li key={p.userId}>
                  <Button className="w-full justify-between" onClick={() => { void ds.signIn(p.role).then(onSignedIn); }}>
                    <span>{p.personName}</span><span className="text-xs text-muted">{titleCase(p.role)}</span>
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        ) : sent ? (
          <div role="status" className="space-y-3 text-sm">
            <p>We sent a sign-in link to <span className="font-medium">{email}</span>. Open the newest email in this same browser and click the link once.</p>
            <Button onClick={onSignedIn}>I have signed in</Button>
          </div>
        ) : !useLink && ds.signInWithPassword ? (
          <form className="space-y-3" onSubmit={(e) => {
            e.preventDefault(); setBusy(true); setError(undefined);
            ds.signInWithPassword!(email, password).then(onSignedIn, (x: Error) => setError(x.message)).finally(() => setBusy(false));
          }}>
            <p className="text-sm text-muted">Use the same email and password as the firm's website. Your firm adds you first.</p>
            <Field id="signin-email" label="Email">
              <input id="signin-email" type="email" required autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} />
            </Field>
            <Field id="signin-password" label="Password" error={error}>
              <input id="signin-password" type="password" required autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} />
            </Field>
            <Button kind="primary" type="submit" disabled={busy} className="w-full">Sign in</Button>
            <div className="flex justify-between text-xs">
              <a href={RESET_PASSWORD_URL} className="text-brand hover:underline">Forgot password?</a>
              <button type="button" className="text-brand hover:underline" onClick={() => { setUseLink(true); setError(undefined); }}>Email me a link instead</button>
            </div>
          </form>
        ) : (
          <form className="space-y-3" onSubmit={(e) => {
            e.preventDefault(); setBusy(true); setError(undefined);
            ds.signIn(email).then(() => setSent(true), (x: Error) => setError(x.message)).finally(() => setBusy(false));
          }}>
            <p className="text-sm text-muted">We email you a one-time link. Your firm adds you first.</p>
            <Field id="signin-email" label="Email" error={error}>
              <input id="signin-email" type="email" required autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} />
            </Field>
            <Button kind="primary" type="submit" disabled={busy} className="w-full">Email me a link</Button>
            {ds.signInWithPassword && <button type="button" className="text-xs text-brand hover:underline" onClick={() => { setUseLink(false); setError(undefined); }}>Sign in with a password instead</button>}
          </form>
        )}
      </div>
    </main>
  );
}
